import { AnimatePresence, motion } from 'framer-motion';
import React, { useState, useEffect } from 'react';

interface CustomDialogBoxProps {
  trigger?: React.ReactNode;
  enabled?: boolean;
  isOpen?: boolean;
  title: string;
  description?: string;
  children?: React.ReactNode;
  leftButtonText: string;
  rightButtonText: string;
  onLeftClick: () => void;
  onRightClick: () => void;
  onBackdropClicked?: () => void;
}

export const CustomDialogBox: React.FC<CustomDialogBoxProps> = ({
  trigger,
  enabled = true,
  title,
  description,
  isOpen,
  leftButtonText,
  rightButtonText,
  onLeftClick,
  onRightClick,
  onBackdropClicked,
  children,
}) => {
  const [open, setOpen] = useState(false);

  // Close dialog on escape key press. Escape is a dismissal like the backdrop
  // and the close button, so it reports through the same callback — closing
  // silently left consumers believing the dialog was still open, and any state
  // the dismissal should have released stayed held (#10668).
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        onBackdropClicked?.();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onBackdropClicked]);

  useEffect(() => {
    if (isOpen !== undefined) {
      setOpen(isOpen);
    }
  }, [isOpen]);

  const handleClosed = () => {
    setOpen(false);
    onBackdropClicked?.();
  };

  // Handle click outside
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      handleClosed();
    }
  };

  const handleLeftClick = () => {
    onLeftClick();
    handleClosed();
  };

  const handleRightClick = () => {
    onRightClick();
    handleClosed();
  };

  return (
    <>
      <div
        onClick={(e) => {
          e.stopPropagation();
          if (!enabled) return;
          setOpen(true);
        }}
      >
        {trigger || (
          <button className="rounded-md bg-line/40 px-4 py-2 transition-colors hover:bg-line/60 active:bg-line/70">
            Open Dialog
          </button>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex w-screen items-center justify-center px-4">
          <AnimatePresence>
            {/* Backdrop */}
            <div
              key="backdrop"
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleBackdropClick}
            />

            {/* Dialog */}
            <motion.div
              key="dialog"
              initial={{ x: 0, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ opacity: 0.2 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="relative z-50 w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all"
            >
              {/* Close button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClosed();
                }}
                className="absolute top-4 right-4 text-inkDisabled hover:text-inkMuted"
              >
                <svg
                  className="size-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Content */}
              <div className="mt-2">
                <h3 className="text-lg leading-6 font-bold text-ink">
                  {title}
                </h3>
                {description && (
                  <p className="mt-2 text-sm text-inkMuted">{description}</p>
                )}
                {children}
              </div>

              {/* Buttons */}
              <div className="mt-6 flex justify-end space-x-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLeftClick();
                  }}
                  className="px-4 py-2 text-red-600 transition-colors hover:text-red-700"
                >
                  {leftButtonText}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRightClick();
                  }}
                  className="px-4 py-2 text-brand transition-colors hover:text-brand"
                >
                  {rightButtonText}
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      )}
    </>
  );
};
